import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type { UseQueryOptions, UseQueryResult } from 'react-query';
import type { ServiceProviderPaymentsControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ReceiptBatchPaymentState } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { IServiceProviderPaymentsApi } from './api';
import { ServiceProviderPaymentsApi } from './api';

const ApiContext = createContext<IServiceProviderPaymentsApi | undefined>(
  undefined
);
const {
  useApi: useServiceProviderPaymentsApi,
  ApiProvider: ServiceProviderPaymentsApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderStripe',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderPaymentsApi(tenantId, auth, apiUrl),
});

export {
  useServiceProviderPaymentsApi,
  ServiceProviderPaymentsApiProvider,
  ApiContext as ServiceProviderPaymentsApiContext,
};
export type { IServiceProviderPaymentsApi } from './api';

const SERVICE_PROVIDER_PAYMENT_RECEIPT_BATCH_KEY = (
  receiptBatchId: string | undefined
) => [`service-provider-payment-receipt-batch`, receiptBatchId];

export const useServiceProviderReceiptBatchPayment = (
  params: {
    receiptBatchId: string | undefined;
  },
  options?: UseQueryOptions<
    ServiceProviderPaymentsControllerResultTypes['getReceiptBatchPaymentStatus']
  >
): UseQueryResult<
  ServiceProviderPaymentsControllerResultTypes['getReceiptBatchPaymentStatus']
> => {
  return useApiQuery(
    // eslint-disable-next-line @grncdr/react-hooks/rules-of-hooks
    () => useServiceProviderPaymentsApi(),
    SERVICE_PROVIDER_PAYMENT_RECEIPT_BATCH_KEY(params.receiptBatchId),
    (api) => {
      if (params.receiptBatchId === undefined) {
        throw new Error('receiptBatchId is undefined');
      }
      return api?.getReceiptBatchPaymentStatus({
        params: {
          receiptBatchId: params.receiptBatchId,
        },
      }) as Promise<
        ServiceProviderPaymentsControllerResultTypes['getReceiptBatchPaymentStatus']
      >;
    },
    {
      select: (data) => {
        if (data.status === ReceiptBatchPaymentState.PAYMENT_INITIATED) {
          return {
            ...data,
            payment: {
              ...data.payment,
              created: new Date(data.payment.created),
              updated: new Date(data.payment.updated),
              canceled: data.payment.canceled
                ? new Date(data.payment.canceled)
                : null,
              completedAt: data.payment.completedAt
                ? new Date(data.payment.completedAt)
                : null,
            },
          };
        }
        return data;
      },
      ...options,
      enabled: !!params.receiptBatchId,
    }
  );
};
